// Phase 282d — Star Academy badge tile for the academy_owner Dashboard.
// Surfaces the most-recent quarter this academy podiumed in. Hides silently
// when the academy has no podium history (zero noise — only celebrates wins).
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Medal, Award } from 'lucide-react';

const RANK_VISUALS = {
  1: {
    label: '🥇 #1 STAR ACADEMY',
    badgeClass: 'bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-500 text-white',
    cardClass:
      'border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-950/30 dark:via-yellow-950/30 dark:to-orange-950/30',
    Icon: Trophy,
    iconColor: 'text-amber-500',
  },
  2: {
    label: '🥈 #2 STAR ACADEMY',
    badgeClass: 'bg-gradient-to-r from-slate-300 via-slate-400 to-slate-500 text-white',
    cardClass:
      'border-slate-300 dark:border-slate-600 bg-gradient-to-br from-slate-50 via-zinc-50 to-slate-100 dark:from-slate-900/40 dark:via-zinc-900/40 dark:to-slate-800/40',
    Icon: Medal,
    iconColor: 'text-slate-500',
  },
  3: {
    label: '🥉 #3 STAR ACADEMY',
    badgeClass: 'bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white',
    cardClass:
      'border-orange-300 dark:border-orange-700 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-orange-950/30 dark:via-amber-950/30 dark:to-yellow-950/30',
    Icon: Award,
    iconColor: 'text-orange-500',
  },
};

export default function StarAcademyTile() {
  const [badge, setBadge] = useState(undefined); // undefined = loading; null = no badge

  useEffect(() => {
    let cancelled = false;
    api.get('/academy/star-badge')
      .then(res => {
        if (cancelled) return;
        setBadge(res.data?.badge || null);
      })
      .catch(() => {
        if (cancelled) return;
        setBadge(null);
      });
    return () => { cancelled = true; };
  }, []);

  // Silently hide while loading or if no podium history
  if (badge === undefined || badge === null) return null;

  const visuals = RANK_VISUALS[badge.rank] || RANK_VISUALS[3];
  const { Icon } = visuals;

  return (
    <Card
      data-testid="star-academy-tile"
      className={`${visuals.cardClass} relative overflow-hidden`}
    >
      <div className="absolute -top-4 -right-4 h-24 w-24 rounded-full bg-white/30 dark:bg-black/20 blur-2xl pointer-events-none" />
      <CardContent className="p-5 relative">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-white/80 dark:bg-slate-900/40 backdrop-blur flex items-center justify-center shadow-sm flex-shrink-0">
            <Icon className={`h-7 w-7 ${visuals.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <span
              className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${visuals.badgeClass}`}
              data-testid="star-academy-rank-pill"
            >
              {visuals.label}
            </span>
            <h3 className="text-lg font-display font-semibold text-slate-900 dark:text-slate-100 mt-1.5">
              {badge.quarter_label}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              <strong>{badge.total}</strong> portal activations · <strong>{badge.velocity}</strong>/day velocity
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
